import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';


const CreateAssignProcess = (props) => {
  const { is_modal_open,
    handleModal,
    is_loading,
    handleSubmit,
    handleUpdate,
    c_action,
    assign_process_id,
    selected_data,
    company_list,
    process_list,
    stage_list,
    sidebar_menu_list
  } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Stores selections: { [stageId]: { sidebar_menu_id, display_order, checked: boolean } }
  // To handle duplicates (same stage in different menus? No, visual implies exclusivity or specific grouping).
  // Implementation: We iterate Sidebar Menus. Inside, we iterate Stages. 
  // Key = `${menuId}-${stageId}` to allow same stage in different menus if needed (though unlikely).
  // Actually, standard is 1 stage -> 1 process assignment. 
  // Let's use a flat map of selected items key by `stage_id`.
  // Wait, if I check "Request" under "Jobs", it means stage "Request" is assigned to "Jobs".
  // If I check "Request" under "AnotherMenu" (if displayed), it would be a conflict?
  // Let's assume stages are unique per usage here.

  const [selectedStages, setSelectedStages] = useState({});
  const [companyId, setCompanyId] = useState('');
  const [processId, setProcessId] = useState('');

  useEffect(() => {
    if (is_modal_open) {
      if (c_action === 'UPDATE' && selected_data) {
        setCompanyId(selected_data.company_id || '');
        setProcessId(selected_data.process_id || '');
        // For update, we might only show the single one being edited, OR we assume we can edit the set.
        // Given the request for "Same Design" (matrix), we try to show the matrix.
        // If selected_data is valid, we pre-select it.
        if (selected_data.stage_id && selected_data.sidebar_menu_id) {
          setSelectedStages({
            [`${selected_data.stage_id}`]: {
              stage_id: selected_data.stage_id,
              sidebar_menu_id: selected_data.sidebar_menu_id,
              display_order: selected_data.display_order,
              checked: true
            }
          });
        }
      } else {
        setCompanyId('');
        setProcessId('');
        setSelectedStages({});
      }
    }
  }, [is_modal_open, c_action, selected_data]);

  const handleCloseModalChange = () => {
    setSelectedStages({});
    setCompanyId('');
    setProcessId('');
    handleModal(false);
  };

  const handleCheckboxChange = (stageId, menuId, checked) => {
    setSelectedStages(prev => {
      const newStages = { ...prev };
      if (checked) {
        newStages[stageId] = {
          stage_id: stageId,
          sidebar_menu_id: menuId,
          display_order: '', // Default empty, user must enter
          checked: true
        };
      } else {
        // If unchecking, remove from selection
        // Check if we are unchecking the exact same menu context
        if (newStages[stageId]?.sidebar_menu_id === menuId) {
          delete newStages[stageId];
        }
      }
      return newStages;
    });
  };

  const handleDisplayOrderChange = (stageId, order) => {
    setSelectedStages(prev => ({
      ...prev,
      [stageId]: {
        ...prev[stageId],
        display_order: order
      }
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!companyId) {
      alert("Please select a company");
      return;
    }
    if (!processId) {
      alert("Please select a process");
      return;
    }

    const itemsToSubmit = Object.values(selectedStages).filter(item => item.checked);
    if (itemsToSubmit.length === 0) {
      alert("Please select at least one stage");
      return;
    }

    // Check display orders
    for (const item of itemsToSubmit) {
      if (!item.display_order) {
        alert("Please enter display order for all selected stages");
        return;
      }
    }

    try {
      // Loop and submit
      // Note: This spawns multiple requests. 
      const promises = itemsToSubmit.map(item => {
        const payload = {
          company_id: Number(companyId),
          process_id: Number(processId),
          stage_id: Number(item.stage_id),
          display_order: Number(item.display_order),
          sidebar_menu_id: item.sidebar_menu_id
        };

        if (c_action === 'UPDATE' && item.stage_id === selected_data?.stage_id) {
          return handleUpdate({
            assign_process_id: assign_process_id,
            updated_data: payload
          });
        } else {
          return handleSubmit(payload);
        }
      });

      await Promise.all(promises);
      handleCloseModalChange();

    } catch (error) {
      console.error("Error submitting", error);
    }
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={is_modal_open}
      onClose={handleCloseModalChange}
      aria-labelledby="responsive-dialog-title"
      component="form"
      onSubmit={onSubmit}
      sx={{
        '& .MuiDialog-paper': {
          width: { md: '350px', xs: '100%' },
        },
      }}
    >
      <DialogTitle
        id="responsive-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '16px',
          fontWeight: 600
        }}
      >
        Assign Stage
        <IconButton size="medium" onClick={handleCloseModalChange}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          paddingTop: '10px!important',
          '& .MuiTextField-root, .MuiFormControl-root': {
            width: '100%',
            fontSize: '14px',
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Company & Process Selects */}
          <FormControl size="small">
            <InputLabel id="company-select-label">Company</InputLabel>
            <Select
              labelId="company-select-label"
              value={companyId}
              label="Company"
              onChange={(e) => setCompanyId(e.target.value)}
            >
              {company_list && company_list.map((company) => (
                <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel id="process-select-label">Process</InputLabel>
            <Select
              labelId="process-select-label"
              value={processId}
              label="Process"
              onChange={(e) => setProcessId(e.target.value)}
            >
              {process_list && process_list.map((process) => (
                <MenuItem key={process.id} value={process.id}>{process.process_name || process.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Grouped Stages */}
          <Box sx={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sidebar_menu_list && sidebar_menu_list.map((menu) => (
              <Box key={menu.id} sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', p: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderBottom: '1px solid #e0e0e0', pb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#757575' }}>Stage</Typography>
                  <Box sx={{ width: '80px', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#757575' }}>Display Order</Typography>
                  </Box>
                </Box>
                <Typography variant="subtitle2" sx={{ color: '#757575', mb: 0.5, px: 0, fontWeight: 600 }}>{menu.side_name}</Typography>
                {stage_list && stage_list.map(stage => {
                  const isChecked = selectedStages[stage.id]?.sidebar_menu_id === menu.id;
                  const isDisabled = selectedStages[stage.id] && selectedStages[stage.id].sidebar_menu_id !== menu.id; // Disable if selected in another group

                  return (
                    <Box key={`${menu.id}-${stage.id}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={(e) => handleCheckboxChange(stage.id, menu.id, e.target.checked)}
                          style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px', accentColor: '#1976d2' }}
                        />
                        <Typography variant="body2" sx={{ color: isDisabled ? '#bdbdbd' : '#757575' }}>{stage.name}</Typography>
                      </Box>
                      <Box sx={{ width: '80px', display: 'flex', justifyContent: 'center' }}>
                        <input
                          type="text"
                          disabled={!isChecked}
                          value={isChecked ? selectedStages[stage.id].display_order : ''}
                          onChange={(e) => {
                            // Allow only numbers
                            const val = e.target.value;
                            if (val === '' || /^\d+$/.test(val)) {
                              handleDisplayOrderChange(stage.id, val);
                            }
                          }}
                          style={{
                            width: '40px',
                            height: '26px',
                            border: '1px solid #c4c4c4',
                            borderRadius: '4px',
                            textAlign: 'center',
                            fontSize: '14px',
                            color: '#757575',
                            outline: 'none',
                            backgroundColor: '#fff'
                          }}
                        />
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>

        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={handleCloseModalChange}
          sx={{
            fontSize: { xs: '12px', sm: '14px' },
            borderColor: "#ff6100", color: "#4c4e52"
          }}
        >
          CANCEL
        </Button>
        <Button
          variant="contained"
          type="submit"
          sx={{
            fontSize: { xs: '12px', sm: '14px' },
            backgroundColor: "#ff6100"
          }}
        >
          SUBMIT
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAssignProcess;